import React, { useEffect, useState } from "react";
import { HiArrowLeft } from "react-icons/hi";
import {
  FaRegCheckCircle,
  FaLock,
  FaCcVisa,
  FaCcMastercard,
  FaCcAmex,
  FaCcPaypal,
} from "react-icons/fa";
import { FiShield, FiZap, FiRefreshCw } from "react-icons/fi";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeElementsOptions, loadStripe } from "@stripe/stripe-js";
import PaymentService from "services/PaymentService";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SubscriptionService from "services/subscription/subscriptionService";
import { displayTimeFormat } from "utils/helperFuncs";

const stripeKey: string | undefined = process.env.REACT_APP_STRIPE_PUB_KEY;

const stripePromise = stripeKey ? loadStripe(stripeKey) : null;
if (!stripeKey) {
  console.error("Stripe public key is not defined.");
}

/* ------------------------------------------------------------------ */
/* Inner form — rendered INSIDE <Elements>, so the Stripe hooks work. */
/* ------------------------------------------------------------------ */
const CheckoutForm = ({ planId }: { planId: string | number }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  console.log("planId:", planId);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${process.env.REACT_APP_FRONTEND_URL}/subscription/success/${planId}`,
      },
    });

    // Only reached on an immediate error; otherwise Stripe redirects to return_url.
    if (error) {
      setMessage(
        error.type === "card_error" || error.type === "validation_error"
          ? (error.message ?? "Your card could not be charged.")
          : "An unexpected error occurred.",
      );
    }
    setSubmitting(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="w-full">
      <PaymentElement id="payment-element" options={{ layout: "tabs" }} />
      <div className="mt-6 flex items-center gap-2">
        <button
          disabled={submitting || !stripe || !elements}
          id="submit"
          className="button-red flex min-w-[160px] items-center justify-center gap-2 rounded-full px-5 py-3 font-semibold text-white shadow-lg shadow-[var(--red)]/30 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 max-sm:w-full"
        >
          <FaLock className="text-[13px]" />
          <span id="button-text">
            {submitting ? "Processing..." : "Pay now"}
          </span>
        </button>
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-[12px] text-gray-400">
        <FaLock className="text-[10px]" />
        Payments are secure and encrypted by Stripe
      </p>
      {message && (
        <div className="mt-6" id="payment-message">
          <span className="text-red-400">{message}</span>
        </div>
      )}
    </form>
  );
};

/* ------------------------------------------------------------------ */
/* Parent — fetches the client secret and renders <Elements>.         */
/* ------------------------------------------------------------------ */
const SubscriptionCheckout = ({ setIsActive }: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const plan = location.state?.plan;

  const [clientSecret, setClientSecret] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const getStripeSubscriptionIntent = async () => {
    if (!plan?.priceId && !plan?.id) {
      setError("Plan information is missing. Please select a plan first.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await SubscriptionService.subscriptionCheckout(plan?.id);

      const secret = response?.data?.clientSecret || response?.clientSecret;
      if (!secret) {
        throw new Error("No client secret received from server");
      }
      setClientSecret(secret);
    } catch (err: any) {
      console.error(
        "Error creating subscription:",
        err?.response?.data || err?.message || err,
      );
      setError(
        err?.response?.data?.message ||
          "Unable to initialize payment. Please check your plan details and try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // --- All hooks declared before any early return ---
  useEffect(() => {
    if (!plan) {
      navigate("/subscription-plan");
    }
  }, [plan, navigate]);

  useEffect(() => {
    if (plan) {
      getStripeSubscriptionIntent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(t);
  }, []);

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: "night",
      variables: {
        colorPrimary: "#f30544",
      },
    },
  };

  if (!plan) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>
          Checkout - {plan?.name ? `${plan.name}` : "AYOZAT Subscription"}
        </title>
        <meta
          name="description"
          content="Subscribe to AYOZAT and unlock premium content."
        />
        <meta property="og:title" content={`${plan?.name} - Checkout`} />
        <meta
          property="og:description"
          content="Subscribe to AYOZAT and unlock premium content."
        />
        <meta property="og:image" content="https://ayozat.co.uk/logo192.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${plan?.name} - Checkout`} />
        <meta
          name="twitter:description"
          content="Subscribe to AYOZAT and unlock premium content."
        />
        <meta name="twitter:image" content="https://ayozat.co.uk/logo192.png" />
        <meta name="twitter:site" content="AYOZAT" />
      </Helmet>

      <div className="mb-40 flex min-h-[90vh] items-start justify-center px-4 py-10 max-sm:mb-0 max-sm:h-auto max-sm:py-6">
        <div className="mx-auto h-auto w-full rounded-2xl border border-white/10 bg-[#0f1117] px-8 py-10 shadow-2xl max-sm:px-5 max-lg:m-0">
          <button
            className="mb-7 flex h-10 w-10 items-center justify-center rounded-full bg-white text-2xl text-black transition-all duration-300 hover:scale-105 hover:bg-gray-200"
            onClick={() => navigate(-1)}
          >
            <HiArrowLeft className="text-black" />
          </button>

          <div className="grid grid-cols-2 gap-10 max-sm:grid-cols-1 max-lg:grid-cols-1">
            {/* Left column - Plan details */}
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.15em] text-[var(--red)]">
                Order Summary
              </p>
              <h1 className="mt-2 text-[22px] font-bold leading-tight text-white">
                {plan?.name || plan?.title}
              </h1>
              <h1 className="mt-1 text-[32px] font-extrabold text-white">
                {plan?.price}
                <span className="ml-1 text-[14px] font-normal text-gray-500">
                  /{displayTimeFormat(plan?.plan_period_format)}
                </span>
              </h1>
              <p className="mt-4 text-[14px] leading-relaxed text-gray-400">
                {plan?.description}
              </p>

              {/* Features list */}
              {plan?.features && (
                <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="h-px flex-1 bg-white/10"></div>
                    <span className="text-[10px] font-semibold tracking-[0.15em] text-gray-500">
                      INCLUDED FEATURES
                    </span>
                    <div className="h-px flex-1 bg-white/10"></div>
                  </div>
                  <ul className="space-y-3">
                    {plan?.features?.map((feature: string, idx: number) => (
                      <li
                        key={idx}
                        className="flex items-center text-[13px] text-gray-300"
                      >
                        <FaRegCheckCircle className="mr-3 flex-shrink-0 text-[14px] text-red-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Right column - Payment / status */}
            <div className="flex flex-col justify-center">
              <h2 className="mb-5 text-[18px] font-bold text-white max-sm:text-center">
                Payment details
              </h2>

              {/* Loading state */}
              {isLoading && (
                <div className="flex flex-col items-center gap-4 py-10">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-700 border-t-red-500"></div>
                  <p className="text-center text-gray-400">
                    Setting up secure payment…
                  </p>
                </div>
              )}

              {/* Error state */}
              {!isLoading && error && (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] p-8">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
                    <svg
                      className="h-8 w-8 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <p className="mb-6 text-center text-[14px] text-gray-400">
                    {error}
                  </p>
                  <button
                    onClick={getStripeSubscriptionIntent}
                    className="button-red rounded-full px-6 py-2 text-[14px] text-white transition-all duration-300 hover:scale-[1.03]"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Initial state - no secret yet */}
              {!isLoading && !error && !clientSecret && (
                <div className="flex flex-col items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-8">
                  <p className="text-center text-gray-400">
                    Click the button below to proceed to payment
                  </p>
                  <button
                    onClick={getStripeSubscriptionIntent}
                    className="button-red rounded-full px-8 py-3 font-medium text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-lg hover:shadow-[var(--red)]/30"
                  >
                    Proceed to Payment
                  </button>
                </div>
              )}

              {/* Payment form - only once we have a client secret */}
              {!isLoading && !error && clientSecret && (
                <Elements stripe={stripePromise} options={options}>
                  <CheckoutForm planId={plan.id} />
                </Elements>
              )}
            </div>
          </div>

          {/* Trust badges */}
          <div className="mt-12 grid grid-cols-3 gap-6 border-t border-white/10 pt-10 max-sm:grid-cols-1">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[var(--red)]/10 text-[var(--red)]">
                <FiShield className="text-[18px]" />
              </div>
              <div>
                <h4 className="text-[15px] font-semibold text-white">
                  Secure & Encrypted
                </h4>
                <p className="mt-1 text-[13px] leading-relaxed text-gray-400">
                  Your payment is protected with bank-level encryption, powered
                  by Stripe.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[var(--red)]/10 text-[var(--red)]">
                <FiRefreshCw className="text-[18px]" />
              </div>
              <div>
                <h4 className="text-[15px] font-semibold text-white">
                  Cancel Anytime
                </h4>
                <p className="mt-1 text-[13px] leading-relaxed text-gray-400">
                  No long-term contracts. Manage or cancel your plan whenever
                  you like.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[var(--red)]/10 text-[var(--red)]">
                <FiZap className="text-[18px]" />
              </div>
              <div>
                <h4 className="text-[15px] font-semibold text-white">
                  Instant Access
                </h4>
                <p className="mt-1 text-[13px] leading-relaxed text-gray-400">
                  Start streaming unlimited content the moment your payment is
                  confirmed.
                </p>
              </div>
            </div>
          </div>

          {/* Accepted payment methods */}
          <div className="mt-10 flex flex-col items-center gap-3 border-t border-white/10 pt-8">
            <p className="text-[12px] font-semibold uppercase tracking-[0.15em] text-gray-500">
              Accepted Payment Methods
            </p>
            <div className="flex items-center gap-4 text-gray-300">
              <FaCcVisa className="text-[34px]" />
              <FaCcMastercard className="text-[34px]" />
              <FaCcAmex className="text-[34px]" />
              <FaCcPaypal className="text-[34px]" />
            </div>
          </div>

          {/* FAQ */}
          {/* <div className="mt-12 border-t border-white/10 pt-10">
            <h3 className="mb-6 text-[20px] font-bold text-white">
              Frequently Asked Questions
            </h3>
            <div className="grid grid-cols-2 gap-x-10 gap-y-4 max-sm:grid-cols-1">
              {[
                {
                  q: "When will I be charged?",
                  a: "You're charged today, and then automatically on the same date each month until you cancel.",
                },
                {
                  q: "Can I cancel my subscription?",
                  a: "Yes — you can cancel anytime from your account settings. You'll keep access until the end of your billing period.",
                },
                {
                  q: "Is my payment information safe?",
                  a: "Absolutely. We never store your card details — all payments are securely processed by Stripe.",
                },
                {
                  q: "What devices can I watch on?",
                  a: "Stream on web, mobile, tablet, and supported smart TVs — anywhere, anytime, in HD.",
                },
              ].map((item, i) => (
                <details
                  key={i}
                  className="group rounded-xl border border-white/10 bg-white/[0.03] p-4 transition-colors hover:bg-white/[0.05]"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between text-[14px] font-semibold text-white">
                    {item.q}
                    <span className="text-gray-500 transition-transform duration-300 group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-[13px] leading-relaxed text-gray-400">
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </div> */}
        </div>
      </div>
    </>
  );
};

export default SubscriptionCheckout;